import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box } from 'lucide-react';
import { LoginForm } from '../components/LoginForm';
import { RegisterForm } from '../components/RegisterForm';
import { LanguageSwitcher } from '@shared/components/LanguageSwitcher';
import { theme } from '@shared/styles/theme';

export type LoginPageMode = 'login' | 'register';

/**
 * LoginPage component - Minecraft themed authentication page
 */
export const LoginPage: React.FC = () => {
  const { t } = useTranslation('auth');
  const location = useLocation();
  const mode: LoginPageMode = location.pathname === '/register' ? 'register' : 'login';
  const navigate = useNavigate();

  const handleAuthSuccess = () => {
    navigate('/dashboard');
  };

  const styles = getStyles();
  return (
    <div style={styles.container}>
      {/* Language Switcher - top right */}
      <div style={styles.langSwitcherWrapper}>
        <LanguageSwitcher />
      </div>

      <div style={styles.content}>
        {/* Logo Section */}
        <div style={styles.logoSection}>
          <div style={styles.logoBox} className="minecraft-panel">
            <Box size={48} color={theme.colors.text.white} strokeWidth={1.5} />
          </div>
          <h1 style={styles.title} className="pixel-font">
            Math Quest
          </h1>
          <p style={styles.subtitle}>
            {mode === 'login' ? t('loginPage.subtitleLogin') : t('loginPage.subtitleRegister')}
          </p>
        </div>

        {/* Tagline — above the form so it's visible without scrolling */}
        <p style={styles.footer}>{t('loginPage.footer')}</p>

        {/* Form Card */}
        <div style={styles.formCard} className="minecraft-panel">
          {mode === 'login' ? (
            <LoginForm onSuccess={handleAuthSuccess} />
          ) : (
            <RegisterForm onSuccess={handleAuthSuccess} />
          )}
        </div>
      </div>
    </div>
  );
};

const getStyles = (): Record<string, React.CSSProperties> => ({
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.background.wood,
    padding: theme.spacing.xl,
    position: 'relative' as const,
  },
  langSwitcherWrapper: {
    position: 'absolute' as const,
    top: theme.spacing.lg,
    right: theme.spacing.xl,
    zIndex: 10,
  },
  content: {
    width: '100%',
    maxWidth: '430px',
  },
  logoSection: {
    textAlign: 'center' as const,
    marginBottom: theme.spacing.xl,
  },
  logoBox: {
    width: '80px',
    height: '80px',
    margin: '0 auto',
    marginBottom: theme.spacing.md,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '12px',
    borderBottom: '4px solid rgba(0, 0, 0, 0.2)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
    backgroundColor: theme.colors.accent.cyan500,
  },
  title: {
    fontSize: theme.fontSize.xxl,
    color: theme.colors.text.primary,
    margin: `0 0 ${theme.spacing.sm} 0`,
  },
  subtitle: {
    fontSize: theme.fontSize.md,
    color: theme.colors.text.secondary,
    margin: 0,
  },
  formCard: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: '24px',
    padding: theme.spacing.xxl,
    marginBottom: theme.spacing.xl,
  },
  footer: {
    textAlign: 'center' as const,
    fontSize: theme.fontSize.sm,
    color: theme.colors.text.secondary,
    margin: 0,
  },
});
